/**
 * Engine-first local-file resolve (#807).
 *
 * Local files must resolve through the Connector on this disk first.
 * Mycelium `/api/library/resolve-file` is second. A cloud 401/offline
 * must never be the sole reason playback fails.
 */

export function shouldAttemptLocalConnectorResolve(connectorAvailable: boolean): boolean {
  return connectorAvailable;
}

/** True when Mycelium resolve-file failed from auth or no-network — not a disk failure. */
export function isCloudAuthOrOfflineError(err: unknown): boolean {
  const status = readErrorStatus(err);
  if (status === 401 || status === 403) return true;
  const msg = String((err as { message?: string })?.message ?? err ?? '');
  return /API 401|API 403|\b401\b|\b403\b|Unauthorized|Failed to fetch|NetworkError|network request failed|offline|ENOTFOUND|ECONNREFUSED|Load failed|Failed to load/i.test(msg);
}

export function localFileResolveFailureMessage(opts: {
  connectorAvailable: boolean;
  localError?: unknown;
  cloudError?: unknown;
  batonAvailable: boolean;
}): string {
  const localMsg = stringifyErr(opts.localError);
  if (opts.connectorAvailable && isCloudAuthOrOfflineError(opts.cloudError)) {
    return localMsg
      ? `Local Connector could not register the file (${localMsg}; cloud 401 ignored)`
      : 'Local Connector could not register the file (cloud 401 ignored)';
  }
  if (localMsg) return localMsg;
  if (!opts.batonAvailable) return 'No BATON connection and file is not local';
  return 'local file resolve failed';
}

function readErrorStatus(err: unknown): number | null {
  if (!err || typeof err !== 'object') return null;
  const status = (err as { status?: unknown }).status;
  return typeof status === 'number' ? status : null;
}

function stringifyErr(err: unknown): string {
  if (err == null) return '';
  return String((err as { message?: string })?.message ?? err).trim();
}
